import React from 'react';
import type {
  KryspaceRenderManifest,
  RenderManifestField,
  RenderManifestQueueItem,
  RenderManifestSection,
} from '../../types/kryspaceRenderManifest';
import styles from './RenderSurface.module.css';

function visibleFields(fields: RenderManifestField[] | undefined): RenderManifestField[] {
  return (fields ?? []).filter((f) => f.tier !== 'mechanical' && f.affordance !== 'hidden');
}

function FieldRow({ field }: { field: RenderManifestField }) {
  const display =
    field.value === null || field.value === undefined ? '—' : String(field.value);
  return (
    <div className={styles.fieldRow} data-tier={field.tier}>
      <dt className={styles.fieldLabel}>{field.label}</dt>
      <dd className={styles.fieldValue}>{display}</dd>
    </div>
  );
}

function QueueList({ items }: { items: RenderManifestQueueItem[] }) {
  if (items.length === 0) {
    return <p className={styles.emptyQueue}>Nothing needs attention.</p>;
  }
  return (
    <ul className={styles.queueList}>
      {items.map((item) => (
        <li key={item.itemId} className={styles.queueItem}>
          <div className={styles.queueTitle}>
            {item.title}
            {item.recommended ? (
              <span className={styles.recommendedBadge}>Recommended</span>
            ) : null}
          </div>
          <div className={styles.queueMeta}>
            <span className={styles.statusPhrase}>{item.statusPhrase}</span>
            {item.priority ? <span className={styles.priority}>{item.priority}</span> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SectionBlock({ section }: { section: RenderManifestSection }) {
  const fields = visibleFields(section.fields);
  return (
    <section className={styles.section} aria-labelledby={`section-${section.id}`}>
      <h2 id={`section-${section.id}`} className={styles.sectionTitle}>
        {section.title}
      </h2>
      {fields.length > 0 ? (
        <dl className={styles.fieldList}>
          {fields.map((field) => (
            <FieldRow key={field.key} field={field} />
          ))}
        </dl>
      ) : null}
      {section.queue ? <QueueList items={section.queue} /> : null}
    </section>
  );
}

export interface RenderManifestViewProps {
  manifest: KryspaceRenderManifest;
}

/** Pure renderer for a kryspace-derived manifest — no derivation logic in the UI. */
export const RenderManifestView: React.FC<RenderManifestViewProps> = ({ manifest }) => {
  return (
    <article className={styles.manifest} data-read-only={manifest.readOnly}>
      <header className={styles.manifestHeader}>
        <h1 className={styles.manifestTitle}>{manifest.title}</h1>
        <p className={styles.manifestMeta}>
          <span>{manifest.workspaceSlug}</span>
          <span aria-hidden="true"> · </span>
          <time dateTime={manifest.generatedAt}>
            {new Date(manifest.generatedAt).toLocaleString()}
          </time>
          <span className={styles.readOnlyBadge}>Read-only</span>
        </p>
      </header>
      {manifest.sections.map((section) => (
        <SectionBlock key={section.id} section={section} />
      ))}
    </article>
  );
};

export default RenderManifestView;
